// Facet registry (nav-restructure, knowledge/07 + master-spec cat 48) — the per-level facet
// tab-set rendered directly under the node title in the content area (GitHub/Linear/Vercel
// model). Sections moved OUT of the sidebar (SideNav = scope tree + mounted apps only); this
// module is now the single owner of the level→facet mapping and of the typed route builder the
// tabs navigate with (extracted from SideNav's old sectionTarget so there is exactly one).
//
// Level-dependent by design (locked D2): each level shows only the facets meaningful there.
// `Overview` is the node home (child cards, locked D1 hybrid); project-level `Apps` is the
// mounted-apps STATUS dashboard (locked D3 — the sidebar keeps the launcher; this tab shows
// status/highlights). Hiding a tab here does not remove its established route, so saved deep links
// remain compatible.

export type ScopeLevel = "workspace" | "client" | "engagement" | "project";

export interface FacetDef {
  key: string;
  label: string;
  /** Trailing route segment, e.g. "/data". Overview = "/home" (the node home route). Also used
   *  for active-tab matching (pathname endsWith base+seg). */
  seg: string;
  /** Honest staging hint — rendered as a "soon" chip on the tab (placeholder page behind it). */
  soon?: boolean;
  /** Presentation-only pause. The facet definition, route and access checks remain available. */
  hidden?: boolean;
  /** Route override for facets whose target is not `base+seg` as a literal route path. Wins over
   *  the default facetTarget(). */
  target?: (ids: ScopeIds) => object;
}

const OVERVIEW: FacetDef = { key: "overview", label: "Overview", seg: "/home" };
const DATA: FacetDef = { key: "data", label: "Upload Files", seg: "/data" };
const DECISIONS: FacetDef = { key: "decisions", label: "Opportunity Board", seg: "/decisions" };
// Keep established route segments for deep-link compatibility; product language is owned at this
// navigation boundary so page reorganisation does not invalidate bookmarks or mounted-app links.
const INTEL: FacetDef = { key: "intel", label: "Client Intelligence", seg: "/intel" };
const ACTIVITY: FacetDef = { key: "activity", label: "Activities", seg: "/activity" };
const GOVERNANCE: FacetDef = { key: "governance", label: "Governance", seg: "/governance" };
const KNOWLEDGE: FacetDef = { key: "knowledge", label: "Data Intelligence", seg: "/knowledge" };
const PERLAB_IP: FacetDef = { key: "kearney-ip", label: "PERLab IP", seg: "/kearney-ip" };
const APPS: FacetDef = { key: "apps", label: "Apps", seg: "/apps" };

/** Approved scope-specific tab visibility and order. Route registrations remain independent of this
 *  display map so a facet removed from a row can still honor an existing bookmark. */
export const FACETS_BY_LEVEL: Record<ScopeLevel, FacetDef[]> = {
  workspace: [OVERVIEW, PERLAB_IP, ACTIVITY, GOVERNANCE],
  client: [OVERVIEW, INTEL, PERLAB_IP, DECISIONS, ACTIVITY, GOVERNANCE],
  engagement: [OVERVIEW, PERLAB_IP, DECISIONS, ACTIVITY, GOVERNANCE],
  project: [OVERVIEW, DATA, APPS, KNOWLEDGE, DECISIONS, ACTIVITY, GOVERNANCE],
};

export interface ScopeIds {
  ws: string;
  client: string;
  eng: string;
  project: string;
}

/**
 * {to, params} route for a facet at the current level (moved from SideNav.sectionTarget).
 *
 * §6 node-native (R4a): when a current NODE id is present (the shell is on a /w/$ws/n/$node route),
 * the facet is addressed by the node's id via the generic route — free-shape safe, and the URL stays
 * on the /n/ family so shell nav is consistent (matches R2 tree + R3 apps). Overview ("/home") maps to
 * the node-home route; every other facet maps to /w/$ws/n/$node/$section. Absent a node (a typed
 * deep-link route), the original type-aware typed builder is used unchanged — a client-LESS
 * engagement (cross-client host, A5/A6) still drops the /c/$client segment.
 */
export function facetTarget(level: ScopeLevel, ids: ScopeIds, seg: string, node?: string): object {
  if (node) {
    if (seg === "/home") return { to: "/w/$ws/n/$node/home", params: { ws: ids.ws, node } };
    return { to: "/w/$ws/n/$node/$section", params: { ws: ids.ws, node, section: seg.replace(/^\//, "") } };
  }
  if (level === "project")
    return ids.client
      ? { to: `/w/$ws/c/$client/e/$eng/p/$project${seg}`, params: { ws: ids.ws, client: ids.client, eng: ids.eng, project: ids.project } }
      : { to: `/w/$ws/e/$eng/p/$project${seg}`, params: { ws: ids.ws, eng: ids.eng, project: ids.project } };
  if (level === "engagement")
    return ids.client
      ? { to: `/w/$ws/c/$client/e/$eng${seg}`, params: { ws: ids.ws, client: ids.client, eng: ids.eng } }
      : { to: `/w/$ws/e/$eng${seg}`, params: { ws: ids.ws, eng: ids.eng } };
  if (level === "client") return { to: `/w/$ws/c/$client${seg}`, params: { ws: ids.ws, client: ids.client } };
  return { to: `/w/$ws${seg}`, params: { ws: ids.ws } };
}

/** The real-id URL prefix of the current level — for active-tab matching against the pathname.
 *  §6 (R4a): on a /n/$node route the prefix is /w/$ws/n/$node, so base+seg matches the actual
 *  node-native pathname (fixes the R2 regression where a typed base never matched a /n/ pathname
 *  and left every facet tab un-highlighted on the tree-nav landing). */
export function facetLevelBase(level: ScopeLevel, ids: ScopeIds, node?: string): string {
  if (node) return `/w/${ids.ws}/n/${node}`;
  if (level === "project") {
    return ids.client
      ? `/w/${ids.ws}/c/${ids.client}/e/${ids.eng}/p/${ids.project}`
      : `/w/${ids.ws}/e/${ids.eng}/p/${ids.project}`;
  }
  if (level === "engagement") return ids.client ? `/w/${ids.ws}/c/${ids.client}/e/${ids.eng}` : `/w/${ids.ws}/e/${ids.eng}`;
  if (level === "client") return `/w/${ids.ws}/c/${ids.client}`;
  return `/w/${ids.ws}`;
}
